import { omit } from 'lodash';
import { withAPIAccount } from '../../lib/http/api';
import { parsedResponse } from '../../lib/http/http';
import { AccountType } from '../../model';
import {
  IApiAddNoticeBody,
  IAPICommonCosConfigRes,
  IAPIGameSdkVersionOption,
  IApiGCloudGameItem,
  IApiMyoaWorkItem,
  IApiNoticeCreateRes,
  IApiNoticeItem,
  IApiNoticeListRes,
  IApiUpdateNoticeBody,
  IQuerySdkBody,
  NoticeSystemStatus,
} from '../../model/api-modules';
import {
  ADMIN_GCLOUD_PROXY,
  ADMIN_NOTICE_PREFIX,
  ADMIN_PREFIX,
} from '../config';

export const getNoticeList = (): Promise<IApiNoticeItem[]> =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_NOTICE_PREFIX}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IApiNoticeListRes>(response, 'data');
      if (res.noticeList) {
        return [...res.noticeList]
          .sort((a, b) => (a.ModifyTime < b.ModifyTime ? 1 : -1))
          .map((notice) => {
            const approvalInfo = res.gameApprovalList.find(
              (approval) => approval.NoticeSystemID === notice.id
            );
            return {
              ...notice,
              approvalId: approvalInfo?.id,
              GameID: approvalInfo?.GameID,
              MyOAStatus: approvalInfo?.MyOAStatus,
              MyOAApprovedMembers: approvalInfo?.MyOAApprovedMembers,
            };
          });
      }
      return [];
    } catch (error) {
      return [];
    }
  });

export const getNoticeDetail = (id: string) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_NOTICE_PREFIX}/${id}`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IApiNoticeItem>(response, 'data');
      return res;
    } catch (error) {
      return null;
    }
  });

export const getMyOaList = () =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_NOTICE_PREFIX}/myoa/workitem/list`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<readonly IApiMyoaWorkItem[]>(
        response,
        'data'
      );
      return res;
    } catch (error) {
      return [];
    }
  });

export const closeMyOa = (noticeID: number) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_NOTICE_PREFIX}/myoa/workitem/close?noticeID=${noticeID}`;
      const response = await api.commonRequest('PUT', url);
      await parsedResponse<IApiNoticeItem>(response, 'data');
      return true;
    } catch (error) {
      return false;
    }
  });

export const addNotice = (body: IApiAddNoticeBody) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/notice-system`;
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<IApiNoticeCreateRes>(response, 'data');
      return res;
    } catch (error) {
      return false;
    }
  });

export const updateNotice = (body: IApiUpdateNoticeBody) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/notice-system/${body.id}`;
      const response = await api.commonRequest('PUT', url, omit(body, 'id'));
      await parsedResponse<IApiNoticeListRes>(response, 'data');
      return true;
    } catch (error) {
      return false;
    }
  });

export const approvalNotice = (
  id: string,
  body: {
    toStatus: NoticeSystemStatus;
    reasonReject?: string;
    mailContentPDFLink?: string;
  }
) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/notice-system/${id}`;
      const response = await api.commonRequest('PATCH', url, omit(body, 'id'));
      await parsedResponse<IApiNoticeListRes>(response, 'data');
      return true;
    } catch (error) {
      return false;
    }
  });

export const noticeEmailPreview = (id: number) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/notice-system/${id}/email-review`;
      const response = await api.commonRequest('GET', url);
      await parsedResponse<IApiNoticeListRes>(response, 'data');
      return true;
    } catch (error) {
      return false;
    }
  });

export const getGCloudSdkList = (body: IQuerySdkBody) =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_GCLOUD_PROXY}/sdk/get_list`;
      const response = await api.commonRequest('POST', url, body);
      const res = await parsedResponse<{ list: IApiGCloudGameItem[] }>(
        response,
        'data'
      );
      return res.list;
    } catch (error) {
      return [];
    }
  });

export const getGameSdkVersion = () =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_GCLOUD_PROXY}/sdk/get_game_and_sdk_version`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IAPIGameSdkVersionOption>(
        response,
        'data'
      );
      return res;
    } catch (error) {
      return;
    }
  });

export const getNoticeCosConfig = () =>
  withAPIAccount(AccountType.auto, async (api) => {
    try {
      const url = `${ADMIN_PREFIX}/cos/sts/notice-system/config`;
      const response = await api.commonRequest('GET', url);
      const res = await parsedResponse<IAPICommonCosConfigRes>(
        response,
        'data'
      );
      return res;
    } catch (error) {
      console.log(error);
      return;
    }
  });
